import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  motion, AnimatePresence, useMotionValue, useTransform, animate,
} from "framer-motion";
import {
  Activity, Users, Zap, Plane, Wrench, Shield, AlertTriangle,
  Building, Radio, BarChart3, ChevronLeft, ChevronRight, X,
  Play, Pause, ExternalLink, CheckCircle, Menu as MenuIcon, ShieldCheck,
  BookOpen, Download, Brain, TrendingUp, Clock, Moon, Layers,
  Globe, Target, MapPin, Star, ArrowRight, AlertOctagon,
} from "lucide-react";

// ─── Image map per department (subtle right-panel backgrounds) ────────────────
const DEPT_IMAGES = {
  enterprise:          "https://images.unsplash.com/photo-1662242723207-13ad21d3816f?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1400",
  "crew-ops":          "https://images.unsplash.com/photo-1713327023682-d327a6961012?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1400",
  "pairing-optimizer": "https://images.pexels.com/photos/33175095/pexels-photo-33175095.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  fairness:            "https://images.unsplash.com/photo-1713327023704-c31f941219fc?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1400",
  disruption:          "https://images.unsplash.com/photo-1662242740676-fa882c6555da?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1400",
  "flight-ops":        "https://images.unsplash.com/photo-1767053853571-fbc0f931855e?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1400",
  "ops-map":           "https://images.unsplash.com/photo-1761203632753-976368cb66b5?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1400",
  engineering:         "https://images.unsplash.com/photo-1773472370698-ef1b90713ad2?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1400",
  camo:                "https://images.unsplash.com/photo-1773472370698-ef1b90713ad2?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1400",
  safety:              "https://images.unsplash.com/photo-1683971113886-ca5883e598b6?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1400",
  "ground-ops":        "https://images.unsplash.com/photo-1652483614757-1c7d1aafa996?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1400",
  irrops:              "https://images.unsplash.com/photo-1662242723207-13ad21d3816f?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1400",
  staffing:            "https://images.unsplash.com/photo-1530469353049-18df0403f42b?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1400",
};

const HERO_BG = "https://images.unsplash.com/photo-1761813409441-5c157440ab98?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1600";
const SCIENCE_BG = "https://images.unsplash.com/photo-1662242723207-13ad21d3816f?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1600";

// ─── Glossary ─────────────────────────────────────────────────────────────────
const GLOSSARY = [
  { abbr:"FRMS",  full:"Fatigue Risk Management System — structured approach to managing crew fatigue risk to an acceptable level" },
  { abbr:"FDP",   full:"Flight Duty Period — total time from report-on-duty until end of last flight. Max 13h domestic (DGCA)" },
  { abbr:"FTL",   full:"Flight Time Limitations — maximum flying hours per period (e.g. 900h/year, 100h/28 days)" },
  { abbr:"FDTL",  full:"Flight Duty Time Limitations — DGCA India's overarching framework combining FTL + rest requirements" },
  { abbr:"DGCA",  full:"Directorate General of Civil Aviation — India's aviation regulatory authority" },
  { abbr:"CAR",   full:"Civil Aviation Requirements — DGCA regulatory publications (e.g. CAR Section 7 Series M)" },
  { abbr:"WOCL",  full:"Window of Circadian Low — 0200–0559 local time when human alertness is at its physiological minimum" },
  { abbr:"ARC",   full:"Airworthiness Review Certificate — annual certificate confirming aircraft continued airworthiness" },
  { abbr:"MEL",   full:"Minimum Equipment List — approved list of items that may be inoperative for dispatch (Cat A/B/C/D)" },
  { abbr:"AMP",   full:"Aircraft Maintenance Programme — CAMO-approved schedule of all required maintenance tasks" },
  { abbr:"SMS",   full:"Safety Management System — systematic process for managing safety risks and promoting a safety culture" },
  { abbr:"IRROPS",full:"Irregular Operations — any deviation from the published schedule (e.g. weather, AOG, diversion)" },
  { abbr:"FO",    full:"First Officer — Second-in-command pilot; right-hand seat" },
  { abbr:"AME",   full:"Aircraft Maintenance Engineer — licensed engineer authorised to certify maintenance work" },
  { abbr:"CAMO",  full:"Continuing Airworthiness Management Organisation — entity managing ongoing aircraft airworthiness" },
  { abbr:"AOG",   full:"Aircraft on Ground — aircraft grounded due to unserviceability, unable to operate revenue flights" },
  { abbr:"SEP",   full:"Safety and Emergency Procedures — mandatory annual crew training on evacuation, fire, and emergencies" },
  { abbr:"CRM",   full:"Crew Resource Management — training on crew communication, decision-making, and threat management" },
  { abbr:"DGR",   full:"Dangerous Goods Regulations — IATA rules for transport of hazardous materials by air" },
  { abbr:"SLA",   full:"Service Level Agreement — committed response/resolution time target (e.g. <60s for disruption SLA)" },
  { abbr:"KPI",   full:"Key Performance Indicator — measurable value tracking operational performance" },
  { abbr:"ATPL",  full:"Airline Transport Pilot Licence — highest pilot licence required for command of commercial aircraft" },
  { abbr:"CPL",   full:"Commercial Pilot Licence — licence required to act as First Officer on commercial operations" },
  { abbr:"IS-BAO",full:"International Standard for Business Aircraft Operations — global safety management framework" },
  { abbr:"QP",    full:"IATA airline designator code (demo context)" },
];

// ─── Font & CSS injection ─────────────────────────────────────────────────────
function useFonts() {
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@300;400;500;700&family=JetBrains+Mono:wght@400;500;700&display=swap";
    document.head.appendChild(link);
    const style = document.createElement("style");
    style.textContent = `
      @keyframes fadeInUp  { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
      @keyframes floatA    { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(20px,-30px) scale(1.05)} }
      @keyframes floatB    { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(-25px,20px) scale(0.97)} }
      @keyframes floatC    { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(15px,25px) scale(1.03)} }
      @keyframes pulse-glow{ 0%,100%{opacity:.18} 50%{opacity:.28} }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(link); document.head.removeChild(style); };
  }, []);
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function darkenHex(hex, f = 0.4) {
  const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16);
  return `rgb(${Math.round(r*f)},${Math.round(g*f)},${Math.round(b*f)})`;
}

function AnimatedCounter({ to, suffix = "" }) {
  const count = useMotionValue(0);
  const rounded = useTransform(count, v => Math.round(v));
  useEffect(() => { const c = animate(count, to, { duration: 1.8, ease: "easeOut" }); return c.stop; }, [count, to]);
  return <><motion.span>{rounded}</motion.span>{suffix}</>;
}

// ─── Slide data ───────────────────────────────────────────────────────────────
const DEPTS = [
  {
    id:"enterprise", name:"Enterprise Command", Icon:Activity, color:"#3b82f6",
    route:"/dashboard", tagline:"C-Suite Aviation Intelligence",
    problem:"Airline executives lack a single source of truth. Risk data is fragmented across 6+ departments — making it impossible to see the full operational safety picture in real-time.",
    solution:"A live command dashboard aggregating fatigue risk, compliance status, and operational alerts across all departments into one unified real-time view.",
    features:[
      "7-day fatigue risk trend with predictive trajectory across all departments",
      "6-department risk heat map: Flight Ops, Engineering, CAMO, Dispatch, Ground, Crew",
      "Legal vs. Safe gap analysis — identifies compliant-but-unsafe operations",
      "Enterprise Risk Score (0–100) with DGCA benchmark overlay",
      "Live active alerts triage with escalation paths and resolution tracking",
    ],
    innovation:"First unified multi-department risk command layer for Indian aviation — consolidates data from departments that traditionally operate in complete isolation.",
    dgca:"CAR Section 7 Series M",
    metrics:[{label:"Depts",to:6},{label:"KPIs",to:28,suffix:"+"},{label:"Refresh",raw:"Live"}],
  },
  {
    id:"crew-ops", name:"Crew Rostering Platform", Icon:Users, color:"#8b5cf6",
    route:"/roster/crew", tagline:"Next-Generation Crew Scheduling",
    problem:"Manual rostering across 275+ pilots leads to qualification mismatches, FDP violations, and missed compliance checks — risking regulatory action and safety outcomes.",
    solution:"An integrated 8-module suite: profiles → pairings → roster → compliance → disruption → audit, with real-time DGCA rule enforcement at every step.",
    features:[
      "275+ crew profiles: licences, type ratings, airport clearances, training records",
      "Gantt Roster Planner with real-time FDP calculation and drag-and-drop",
      "Pairing Builder for flight duty sequences with automated rule validation",
      "DGCA Compliance Board — automated checking against all FTL limits",
      "Recovery Console, Audit Log, and What-If Scenario Simulator",
    ],
    innovation:"The eligibility engine checks type rating, airport clearances, and training currency simultaneously for every potential assignment — in real-time, before any commitment.",
    dgca:"CAR FTL — FDP ≤13h, rest ≥12h",
    metrics:[{label:"Crew",to:275,suffix:"+"},{label:"Modules",to:8},{label:"Rules",to:12,suffix:"+"}],
  },
  {
    id:"pairing-optimizer", name:"AI Pairing Optimizer", Icon:Layers, color:"#f59e0b",
    route:"/roster/pairings", tagline:"Visual Fleet Rotation Intelligence",
    problem:"Fleet planners build aircraft rotations manually using static spreadsheets — missing utilization windows, flying aircraft into maintenance with no forward-looking gap analysis, and making sub-optimal base allocation decisions.",
    solution:"A visual Gantt-based pairing builder showing all 37 B737 MAX 8 tails in real-time, with Claude AI analyzing the full month schedule and surfacing exactly where to add revenue flights to hit the 9–11h daily utilization target.",
    features:[
      "37 B737 MAX 8 tails — base-grouped fleet board (DEL/BOM/BLR/HYD/MAA/CCU) with live utilization bars",
      "IST-aware Gantt timeline — today's date line, past dimming, AOG/maintenance shown as striped blocks",
      "Present Schedule: current amber rotation blocks with sector labels and FDP hours",
      "Optimised Schedule: Claude AI teal overlay — shows exactly what to move, add, or shift",
      "Gaps View: red idle-window segments — click any gap to see Claude's DGCA-compliant route suggestion",
    ],
    innovation:"First AI-driven fleet rotation optimizer for Indian carriers — Claude analyses 37 tails, computes idle windows, and returns three ranked recommendations with DGCA compliance notes and expected block-hour gain per recommendation.",
    dgca:"DGCA FTL: 9–11h block hours target, 100h/month per tail",
    metrics:[{label:"Aircraft",to:37},{label:"Avg Block",raw:"9.8h/d"},{label:"AI Views",to:3}],
  },
  {
    id:"fairness", name:"Fairness & QoL Engine", Icon:Star, color:"#a855f7",
    route:"/fairness-engine", tagline:"Crew Quality-of-Life Intelligence",
    problem:"Scheduling burden falls disproportionately on certain crew members — fatigue, night shifts, and disruption call-ins concentrate on the same individuals, creating hidden safety risk and triggering attrition among experienced captains.",
    solution:"A real-time QoL Index (0–100) computed for every crew member across four dimensions, with ranking, base comparisons, extremes identification, and direct integration with the assignment engine to enforce scheduling fairness.",
    features:[
      "QoL Score per crew: weighted composite of fatigue (40%), block hours (30%), seniority factor (20%), disruption call-ins (10%)",
      "Live crew ranking table — colour-banded: Burdened / At Risk / Moderate / Well Rested",
      "Score distribution histogram showing scheduling burden spread across the fleet",
      "Base-wise average QoL comparison — identifies systemic over-rostering at specific stations",
      "Extremes panel: most burdened crew + highest-QoL crew for immediate targeting",
    ],
    innovation:"First QoL-aware scheduling layer in Indian aviation — connects fatigue risk data directly to scheduling fairness enforcement, reducing attrition and preventing the hidden safety risk of over-burdened crew operating at the edge of their limits.",
    dgca:"DGCA FRMS fairness principles + IATA FRMS Guide",
    metrics:[{label:"Crew",to:246,suffix:"+"},{label:"QoL Factors",to:4},{label:"Bases",to:6}],
  },
  {
    id:"disruption", name:"AI Disruption Engine", Icon:Zap, color:"#f59e0b",
    route:"/roster/disruption", tagline:"Claude AI-Powered Crew Recovery",
    problem:"Disruptions require immediate, legally-compliant crew changes. Ops controllers currently make these decisions manually under pressure with incomplete qualification information.",
    solution:"Claude AI analyses every disruption across 5 dimensions, scores recovery options, and activates two replacement crew (Captain + FO) from the eligibility engine in under 60 seconds.",
    features:[
      "30+ disruption types: sick call, FDP breach, diversion, AOG, weather, cascade",
      "5-dimensional AI scoring: Compliance · Fatigue · Cost · Delay · Fairness",
      "Qualification eligibility: type rating + airport clearance + training checked",
      "Two-crew simultaneous replacement: Captain and FO found independently",
      "One-click resolution with roster update, assignment creation, and audit trail",
    ],
    innovation:"Claude AI produces DGCA-aware plain-English operational assessments alongside numeric recovery scores — the first such deployment in Indian crew recovery management.",
    dgca:"CAR FTL — only eligible crew activated",
    metrics:[{label:"Types",to:30,suffix:"+"},{label:"AI Dims",to:5},{label:"SLA",to:60,suffix:"s"}],
  },
  {
    id:"flight-ops", name:"Live Flight Ops Board", Icon:Plane, color:"#22c55e",
    route:"/flights", tagline:"Multi-Carrier Real-Time Intelligence",
    problem:"Flight data and crew scheduling live in completely separate systems. Ops controllers have no live view linking which crew is on which flight with real-time FDP visibility — a critical blind spot during operations.",
    solution:"Live Indian carrier flight tracking via AviationStack fused with crew assignments, real-time FDP monitoring, interactive Leaflet map, and full end-to-end disruption simulation — for any Indian scheduled carrier.",
    features:[
      "Live flight tracking for Indian carriers via AviationStack — 100 live flights displayed",
      "Crew-flight simulation bridge: Captain + FO auto-assigned to every live flight",
      "FDP monitoring per crew per flight with 13h DGCA domestic limit visualization",
      "Leaflet map with great-circle routes and estimated aircraft positions across India",
      "Trigger Disruption: convert any live flight into a full disruption scenario in one click",
    ],
    innovation:"First real-time crew-to-flight fusion layer built for Indian carriers and DGCA compliance — any live flight converts into a disruption scenario with DGCA-eligible replacement crew found in under 60 seconds.",
    dgca:"CAR-FTL domestic FDP rules enforced live",
    metrics:[{label:"Flights",to:100},{label:"Airports",to:34},{label:"FDP",raw:"Live"}],
  },
  {
    id:"ops-map", name:"Ops Map & Network Control", Icon:MapPin, color:"#06b6d4",
    route:"/ops-map", tagline:"Live India Network Intelligence",
    problem:"During disruptions, ops controllers have no real-time spatial view of which flights are affected, how the cascade propagates across the network, and which aircraft are physically where — forcing slow, phone-based coordination across bases.",
    solution:"An interactive India operations map with live aircraft positions, AOG cascade flows, colour-coded disruption chains, and direct integration with the recovery console — all visible on a single geographic canvas.",
    features:[
      "Interactive Leaflet map with all airline routes across India's metro and tier-2 airports",
      "AOG Flow visualisation: BOM→AMD hydraulic failure scenario with full cascade chain — delayed, diverted, and at-risk sectors highlighted",
      "Colour-coded route status: Green (active), Amber (at risk), Red (disrupted), Purple (diverted)",
      "One-click Demo Control Panel: inject scenarios, reset to clean state, advance virtual time (+3h FDP simulation)",
      "Disruption event sidebar with live triage, crew legal status, and cascade resolution tracking",
    ],
    innovation:"First India-network-aware disruption map for aviation operations — replaces voice-based coordination with a real-time visual command canvas that makes cascade chains and recovery paths immediately obvious.",
    dgca:"DGCA IRROPS documentation requirements",
    metrics:[{label:"Airports",to:34},{label:"Route Types",to:4},{label:"Scenarios",to:6}],
  },
  {
    id:"engineering", name:"Engineering Risk", Icon:Wrench, color:"#ef4444",
    route:"/engineering", tagline:"AME Fatigue & Maintenance Safety",
    problem:"Aircraft Maintenance Engineers on night shifts accumulate dangerous fatigue levels that are completely invisible to existing safety management systems.",
    solution:"Real-time fatigue monitoring for all AME staff with task risk classification, shift pattern analysis, and pre-emptive Do-Not-Assign alerts before safety-critical tasks.",
    features:[
      "AME fatigue score tracking across all maintenance shifts and task types",
      "Task criticality classification: complex vs. routine vs. airworthiness-critical",
      "Night shift and irregular hours fatigue accumulation model (FRMS-aligned)",
      "Do-Not-Assign alerts for high-risk AMEs on safety-critical maintenance tasks",
      "Workload distribution analysis across hangar, line, and base maintenance",
    ],
    innovation:"Extends FRMS beyond flight crew to maintenance personnel — addressing a safety and regulatory gap that exists across most Indian carriers today.",
    dgca:"CAR-M + ICAO Annex 19 SMS",
    metrics:[{label:"AME Staff",to:45,suffix:"+"},{label:"Risk Tiers",to:4},{label:"Task Types",to:12}],
  },
  {
    id:"camo", name:"CAMO Planning", Icon:Shield, color:"#06b6d4",
    route:"/camo", tagline:"Continuing Airworthiness Management",
    problem:"CAMO teams manage ARC renewals, MEL deferrals, and AMP compliance across disconnected spreadsheets and external MRO systems — with no link to fatigue context or live operational risk.",
    solution:"A fully self-contained CAMO operations module: create and manage tasks natively within Fatigue Shield, or sync automatically via API from your existing MRO system (AMOS, OASES, Ramco, TRAX). Every record carries a source badge.",
    features:[
      "ARC Tracking — per aircraft tail, with renewal alerts at 60 / 30 / 0 days — colour-coded urgency",
      "MEL Deferral Management — Category A/B/C/D with automatic expiry calculation and one-click clearance",
      "AMP Compliance — flight-hour-based check status (A-Check, C-Check, Engine Borescope) per aircraft",
      "Task CRUD — create, assign, update, and close CAMO tasks directly within Fatigue Shield (Option A)",
      "MRO API Integration Ready — AMOS, OASES, Ramco Aviation, TRAX connectors available (Option B)",
    ],
    innovation:"Unique dual-mode architecture: works standalone for carriers with no MRO system, and integrates with incumbent MRO software for larger operators. First Indian CAMO module with fatigue overlay on planner workload.",
    dgca:"CAR-M CAMO requirements",
    metrics:[{label:"Aircraft",to:18,suffix:"+"},{label:"MEL Cats",to:4},{label:"ARC Lead",to:60,suffix:"d"}],
  },
  {
    id:"safety", name:"Safety Analytics", Icon:AlertTriangle, color:"#f97316",
    route:"/safety", tagline:"SMS & Fatigue-Safety Correlation",
    problem:"SMS reports are filed but never cross-referenced with crew fatigue data — the most powerful leading indicator of incidents is consistently overlooked.",
    solution:"Safety analytics correlating fatigue scores with SMS events in real-time, identifying at-risk crew and patterns before incidents occur — with full DGCA audit trail.",
    features:[
      "SMS report intake with automated severity classification across 8 categories",
      "Fatigue-to-incident correlation matrix — identifies fatigue as causal factor",
      "High-risk crew identification with pre-emptive Do-Not-Assign workflow",
      "30/60/90-day rolling safety trend analysis per department and base",
      "Full DGCA safety audit trail — all risk events logged with resolution",
    ],
    innovation:"Fatigue-safety correlation is rare in Indian aviation. This module connects operational fatigue data to SMS outcomes, enabling proactive intervention before incidents.",
    dgca:"DGCA CAR-19 SMS + IS-BAO",
    metrics:[{label:"SMS Cats",to:8},{label:"Correlation",raw:"Live"},{label:"Audit",to:100,suffix:"%"}],
  },
  {
    id:"ground-ops", name:"Ground Ops & Back Office", Icon:Building, color:"#84cc16",
    route:"/ground-ops", tagline:"Station Safety & HR Intelligence",
    problem:"Ground handling and back-office staff face the same shift-work fatigue risks as flight crew — yet they are universally excluded from FRMS implementations.",
    solution:"Dedicated fatigue risk monitoring for Ground Ops (ramp, cargo, fuelling) and Back Office (HR, payroll, dispatch) with AI-powered staffing support.",
    features:[
      "Ground crew fatigue monitoring: ramp, cargo, fuelling, towing, pax services",
      "FRMS-aligned fatigue scoring per ground role with shift pattern analysis",
      "Back office HR integration: contracts, leave tracking, crew records",
      "AI Staffing Formula Engine: demand forecasting with seasonal models",
      "Workload balancing to prevent ground crew fatigue accumulation",
    ],
    innovation:"Ground ops fatigue is a major invisible safety risk. Fatigue Shield is among the first platforms in Indian aviation to address this comprehensively.",
    dgca:"DGCA CAR Section 9 aerodrome standards",
    metrics:[{label:"Roles",to:6,suffix:"+"},{label:"FRMS",raw:"Live"},{label:"Forecast",raw:"12mo"}],
  },
  {
    id:"irrops", name:"IRROPS Console & Alerts", Icon:Radio, color:"#ec4899",
    route:"/irrops", tagline:"Irregular Operations Command",
    problem:"During IRROPS, crew legality decisions are made via phone calls with no structured decision support — creating regulatory exposure and costly cascade delays.",
    solution:"A structured IRROPS command console with disruption triage, crew legal status monitoring, and multi-base coordination — with proactive alerts before limits are breached.",
    features:[
      "Real-time IRROPS event triage with priority classification and ownership",
      "Crew legal status: FDP, rest, and cumulative limits during IRROPS",
      "Proactive alerts: FDP approaching 13h, rest period conflicts, mismatches",
      "Multi-base coordination panel with cross-station crew visibility",
      "Alert escalation with acknowledgement tracking and resolution logging",
    ],
    innovation:"Replaces phone-based IRROPS management with a digital command layer — creating an auditable, regulatory-defensible record of every operational decision made.",
    dgca:"DGCA FDTL dispensation documentation",
    metrics:[{label:"Alert Types",to:15,suffix:"+"},{label:"SLA",to:5,suffix:"min"},{label:"Bases",to:7}],
  },
  {
    id:"staffing", name:"AI Staffing Engine", Icon:BarChart3, color:"#a855f7",
    route:"/staffing-engine", tagline:"Intelligent Crew Demand Forecasting",
    problem:"Staffing decisions rely on static headcount formulas that ignore training pipelines, recurrency calendars, and seasonal demand — leading to chronic crew shortages.",
    solution:"AI-driven staffing model using operational, seasonal, and training variables together to provide true crew availability forecasts — far beyond industry-standard ratios.",
    features:[
      "Fleet-level demand modelling: Captain, First Officer, Cabin Crew by base",
      "Seasonal adjustment: peak, shoulder, and off-peak traffic pattern models",
      "Training pipeline: recurrency, type rating, and initial training slots",
      "Attrition scenario modelling with conservative and growth projections",
      "Gap analysis: current headcount vs. required vs. trained and ready",
    ],
    innovation:"Models training pipeline, recurrency calendar, and seasonal demand together — true crew availability forecasts vs. the simplistic ratio formulas used across the industry.",
    dgca:"DGCA minimum crew ratio requirements",
    metrics:[{label:"Fleets",to:3},{label:"Forecast",to:12,suffix:"mo"},{label:"AI",raw:"GPT-4o"}],
  },
];

const ALL_SLIDES = [
  {id:"hero",type:"hero"},
  {id:"science",type:"science"},
  ...DEPTS.map(d=>({id:d.id,type:"dept",data:d})),
  {id:"cta",type:"cta"},
  {id:"closing",type:"closing"},
];

const slideVariants = {
  enter: d => ({ opacity:0, x: d>0?80:-80 }),
  center: { opacity:1, x:0 },
  exit:  d => ({ opacity:0, x: d>0?-80:80 }),
};
const fromLeft = (delay=0) => ({
  hidden:{ opacity:0, x:-60 },
  show:{ opacity:1, x:0, transition:{ duration:0.55, ease:[0.22,1,0.36,1], delay } },
});
const featureContainer = {
  hidden:{},
  show:{ transition:{ staggerChildren:0.1, delayChildren:0.45 } },
};
const featureItem = {
  hidden:{ opacity:0, x:-50 },
  show:{ opacity:1, x:0, transition:{ duration:0.5, ease:[0.22,1,0.36,1] } },
};

// ─── Background orbs ──────────────────────────────────────────────────────────
function ColorOrbs({ color }) {
  const anims = ["floatA","floatB","floatC"];
  const sizes = [400,280,200];
  const positions = [
    {top:"60%",right:"-60px"},
    {top:"10%",right:"15%"},
    {top:"40%",right:"30%"},
  ];
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {sizes.map((sz,i) => (
        <div key={i} style={{
          position:"absolute", width:sz, height:sz, borderRadius:"50%",
          background: color, filter:"blur(80px)", opacity:0.09,
          animation:`${anims[i]} ${8+i*3}s ease-in-out infinite`,
          ...positions[i],
        }}/>
      ))}
    </div>
  );
}

// ─── Hero Slide ───────────────────────────────────────────────────────────────
function HeroSlide({ onNext }) {
  const stats = [
    {label:"Modules",to:22,suffix:"+"},
    {label:"Crew Managed",to:275,suffix:"+"},
    {label:"Disruption Types",to:30,suffix:"+"},
    {label:"Departments",to:13},
  ];
  return (
    <div className="relative w-full h-full flex flex-col overflow-hidden" style={{background:"#030c1a"}}>
      <div className="absolute inset-0"
        style={{backgroundImage:`url(${HERO_BG})`,backgroundSize:"cover",backgroundPosition:"center 40%",opacity:0.40}}/>
      <div className="absolute inset-0" style={{background:"linear-gradient(to right, #030c1a 35%, transparent 70%)"}}/>
      <div className="absolute inset-0" style={{background:"linear-gradient(to top, #030c1a 20%, transparent 60%)"}}/>
      <div className="absolute inset-0 opacity-[0.04]"
        style={{backgroundImage:"linear-gradient(#3b82f6 1px,transparent 1px),linear-gradient(90deg,#3b82f6 1px,transparent 1px)",backgroundSize:"80px 80px"}}/>

      <div className="relative z-10 flex flex-col justify-center h-full px-20 pt-16 pb-12">
        <motion.div variants={fromLeft(0.1)} initial="hidden" animate="show"
          className="inline-flex items-center gap-2 mb-8 w-fit"
          style={{background:"rgba(59,130,246,0.12)",border:"1px solid rgba(59,130,246,0.3)",
            borderRadius:"2px",padding:"6px 16px",fontFamily:"JetBrains Mono,monospace",fontSize:"11px",color:"#60a5fa",letterSpacing:"0.12em"}}>
          <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse"/>
          INDIAN AVIATION · OPERATIONS PLATFORM
        </motion.div>

        <motion.h1 data-testid="hero-slide-title"
          variants={fromLeft(0.2)} initial="hidden" animate="show"
          className="font-black uppercase text-white"
          style={{fontSize:"clamp(64px,9vw,120px)",lineHeight:0.88,letterSpacing:"-0.04em"}}>
          FATIGUE
          <span className="block" style={{color:"#3b82f6",textShadow:"0 0 80px rgba(59,130,246,0.5)"}}>SHIELD</span>
        </motion.h1>

        <motion.p variants={fromLeft(0.35)} initial="hidden" animate="show"
          className="mt-6 text-[#8ba3cb] max-w-lg"
          style={{fontSize:"20px",lineHeight:1.6}}>
          Sovereign AI-Enabled Aviation Operations &amp; Crew Management Platform
        </motion.p>

        <motion.div variants={fromLeft(0.5)} initial="hidden" animate="show"
          className="flex gap-6 mt-14">
          {stats.map((s,i)=>(
            <div key={i} className="flex flex-col"
              style={{borderLeft:`3px solid ${i===0?"#3b82f6":"rgba(255,255,255,0.12)"}`,paddingLeft:"16px"}}>
              <div className="text-white font-black" style={{fontFamily:"JetBrains Mono,monospace",fontSize:"48px",lineHeight:1}}>
                <AnimatedCounter to={s.to} suffix={s.suffix||""}/>
              </div>
              <div className="text-[#8ba3cb] uppercase tracking-widest mt-1" style={{fontFamily:"JetBrains Mono,monospace",fontSize:"11px"}}>
                {s.label}
              </div>
            </div>
          ))}
        </motion.div>

        <motion.div variants={fromLeft(0.65)} initial="hidden" animate="show" className="flex items-center gap-4 mt-12">
          <button onClick={onNext} data-testid="hero-begin-button"
            className="px-10 py-4 text-white font-bold uppercase tracking-widest transition-all hover:scale-[1.03]"
            style={{background:"#3b82f6",fontFamily:"JetBrains Mono,monospace",fontSize:"13px",
              boxShadow:"0 0 40px rgba(59,130,246,0.45)",borderRadius:"2px"}}>
            Begin Presentation <ChevronRight size={15} className="inline ml-1"/>
          </button>
          <span className="text-[#8ba3cb]/50" style={{fontFamily:"JetBrains Mono,monospace",fontSize:"11px"}}>
            or press → / spacebar
          </span>
        </motion.div>
      </div>
    </div>
  );
}

// ─── Fatigue Science Slide ─────────────────────────────────────────────────────
const SCORE_FACTORS = [
  { Icon:Clock,     color:"#3b82f6", label:"Time Since Last Sleep", weight:"40%",
    desc:"Hours awake since last valid sleep opportunity (≥7h uninterrupted). Score rises sharply after 14h — every 2h beyond adds ~10 risk points. Capped at 20h awake = 100 points." },
  { Icon:TrendingUp,color:"#8b5cf6", label:"Cumulative Duty Hours",  weight:"25%",
    desc:"Rolling 7-day and 28-day flight hours vs. DGCA limits (60h/7d, 190h/28d). Breaching >80% of limit adds significant risk. Prevents schedule gaming that hides chronic fatigue." },
  { Icon:Moon,      color:"#f59e0b", label:"Circadian Disruption",   weight:"20%",
    desc:"Penalty for duty starting or operating through the WOCL (0200–0559 local). Human alertness is at its physiological minimum during this window — each overlapping hour adds 12.5 points." },
  { Icon:Layers,    color:"#22c55e", label:"Rest Adequacy",          weight:"10%",
    desc:"Actual rest received vs. DGCA minimum (12h domestic). If rest < 12h, deficit penalty is added. Full penalty triggers at <9h rest. Captures the quality of the rest period, not just its length." },
  { Icon:Brain,     color:"#ec4899", label:"Consecutive Duty Days",  weight:"5%",
    desc:"Days worked consecutively without a full off day. Beyond 5 consecutive days, a compounding fatigue factor is applied. Reflects accumulated sleep debt that individual rest periods cannot fully reverse." },
];

const RISK_BANDS = [
  { range:"0 – 25",  label:"Fully Rested",     color:"#22c55e", action:"No restrictions — normal operations" },
  { range:"26 – 45", label:"Mild Fatigue",      color:"#3b82f6", action:"Monitor — flag for next roster review" },
  { range:"46 – 65", label:"Moderate Fatigue",  color:"#f59e0b", action:"Supervisor review before assignment" },
  { range:"66 – 80", label:"High Fatigue",       color:"#f97316", action:"Restricted duties — no extended FDP" },
  { range:"81 – 100",label:"Critical",           color:"#ef4444", action:"Do Not Assign — mandatory rest required" },
];

function ScienceSlide() {
  return (
    <div className="flex w-full h-full pt-16 overflow-hidden" style={{background:"#060d1a"}}>
      {/* Background image */}
      <div className="absolute inset-0"
        style={{backgroundImage:`url(${SCIENCE_BG})`,backgroundSize:"cover",backgroundPosition:"center",opacity:0.06}}/>
      <div className="absolute inset-0" style={{background:"linear-gradient(135deg,#060d1a 60%,rgba(59,130,246,0.08) 100%)"}}/>

      <div className="relative z-10 w-full h-full flex flex-col px-14 py-8 overflow-y-auto">

        {/* Header */}
        <motion.div variants={fromLeft(0.05)} initial="hidden" animate="show" className="mb-6">
          <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-sm"
            style={{background:"rgba(59,130,246,0.12)",border:"1px solid rgba(59,130,246,0.25)",
              fontFamily:"JetBrains Mono,monospace",fontSize:"10px",color:"#60a5fa",letterSpacing:"0.12em"}}>
            <Brain size={11}/> FRMS · ALGORITHM METHODOLOGY
          </div>
          <h2 className="text-white font-black leading-none"
            style={{fontSize:"clamp(32px,4vw,52px)",letterSpacing:"-0.03em"}}>
            The Science Behind<br/>
            <span style={{color:"#3b82f6"}}>The Fatigue Score</span>
          </h2>
          <p className="text-[#8ba3cb] mt-3 max-w-2xl" style={{fontSize:"15px",lineHeight:1.6}}>
            Every score displayed in Fatigue Shield is a real-time composite of five FRMS-aligned factors derived from
            ICAO Doc 9966 principles and DGCA FDTL requirements. Each factor is independently weighted, then summed to
            produce a 0–100 risk index.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 gap-6 flex-1">

          {/* Left: Formula + Factors */}
          <div className="space-y-3">
            {/* Formula box */}
            <motion.div variants={fromLeft(0.15)} initial="hidden" animate="show"
              className="rounded-sm p-4"
              style={{background:"rgba(255,255,255,0.04)",border:"1px solid rgba(59,130,246,0.2)"}}>
              <div className="text-[10px] uppercase tracking-[0.15em] font-bold mb-3"
                style={{color:"#60a5fa",fontFamily:"JetBrains Mono,monospace"}}>Composite Score Formula</div>
              <div className="font-mono text-white text-sm leading-relaxed"
                style={{fontFamily:"JetBrains Mono,monospace",background:"rgba(0,0,0,0.3)",padding:"10px 14px",borderRadius:"2px",fontSize:"12px"}}>
                Score = (TSS × 0.40)<br/>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;+ (CumulHrs × 0.25)<br/>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;+ (WOCL_Overlap × 0.20)<br/>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;+ (RestDeficit × 0.10)<br/>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;+ (ConsecDays × 0.05)
              </div>
              <p className="text-[#64748b] mt-2" style={{fontSize:"11px",fontFamily:"JetBrains Mono,monospace"}}>
                Each sub-score normalised to 0–100 before weighting. Final score capped at 100.
              </p>
            </motion.div>

            {/* Factors */}
            <motion.ul variants={featureContainer} initial="hidden" animate="show" className="space-y-2">
              {SCORE_FACTORS.map((f,i)=>(
                <motion.li key={i} variants={featureItem}
                  className="rounded-sm p-3 flex gap-3"
                  style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.06)"}}>
                  <div className="shrink-0 w-7 h-7 rounded-sm flex items-center justify-center mt-0.5"
                    style={{background:`${f.color}18`,border:`1px solid ${f.color}40`}}>
                    <f.Icon size={14} style={{color:f.color}}/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-white font-semibold" style={{fontSize:"13px"}}>{f.label}</span>
                      <span className="text-xs px-1.5 py-0.5 rounded font-bold"
                        style={{background:`${f.color}20`,color:f.color,fontFamily:"JetBrains Mono,monospace",fontSize:"10px"}}>
                        {f.weight}
                      </span>
                    </div>
                    <p className="text-[#94a3b8]" style={{fontSize:"12px",lineHeight:1.55}}>{f.desc}</p>
                  </div>
                </motion.li>
              ))}
            </motion.ul>
          </div>

          {/* Right: Risk bands + DGCA note */}
          <div className="space-y-4">
            <motion.div variants={fromLeft(0.2)} initial="hidden" animate="show">
              <div className="text-[10px] uppercase tracking-[0.15em] font-bold mb-3"
                style={{color:"#94a3b8",fontFamily:"JetBrains Mono,monospace"}}>Risk Level Bands (0–100 Index)</div>
              <div className="space-y-2">
                {RISK_BANDS.map((b,i)=>(
                  <div key={i} className="flex items-center gap-3 rounded-sm p-3"
                    style={{background:"rgba(255,255,255,0.03)",border:`1px solid ${b.color}30`}}>
                    <div className="shrink-0 w-1 self-stretch rounded-full" style={{background:b.color}}/>
                    <div className="w-16 shrink-0 font-mono text-white font-bold" style={{fontFamily:"JetBrains Mono,monospace",fontSize:"12px"}}>
                      {b.range}
                    </div>
                    <div>
                      <div className="font-bold" style={{color:b.color,fontSize:"13px"}}>{b.label}</div>
                      <div className="text-[#64748b]" style={{fontSize:"11px"}}>{b.action}</div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div variants={fromLeft(0.35)} initial="hidden" animate="show"
              className="rounded-sm p-4"
              style={{background:"rgba(59,130,246,0.07)",border:"1px solid rgba(59,130,246,0.2)"}}>
              <div className="text-[10px] uppercase tracking-[0.15em] font-bold mb-2"
                style={{color:"#60a5fa",fontFamily:"JetBrains Mono,monospace"}}>Regulatory Basis</div>
              <ul className="space-y-1.5">
                {[
                  "ICAO Doc 9966 FRMS Implementation Guide",
                  "DGCA CAR Section 7 Series M — Crew FDTL",
                  "DGCA CAR-FTL: FDP ≤13h domestic, rest ≥12h",
                  "IATA FRMS Implementation Guide (4th Edition)",
                  "SAFTE™-inspired sleep pressure modelling concepts",
                ].map((t,i)=>(
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle size={12} className="mt-0.5 shrink-0" style={{color:"#3b82f6"}}/>
                    <span style={{color:"#94a3b8",fontSize:"12px"}}>{t}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div variants={fromLeft(0.45)} initial="hidden" animate="show"
              className="rounded-sm p-3"
              style={{background:"rgba(239,68,68,0.07)",border:"1px solid rgba(239,68,68,0.2)"}}>
              <p className="text-[#fca5a5]" style={{fontSize:"12px",lineHeight:1.6}}>
                <strong>Legal vs. Safe Gap:</strong> A crew member may be legally compliant (FDP &lt; 13h) yet
                carry a fatigue score of 70+ due to WOCL overlap and prior cumulative hours. Fatigue Shield
                surfaces this gap — the most dangerous blind spot in regulatory compliance-only frameworks.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Department Slide ─────────────────────────────────────────────────────────
function DeptSlide({ slide, onNavigate }) {
  const { Icon, color, name, tagline, problem, solution, features, innovation, dgca, metrics, route, id } = slide;
  const deptIdx = DEPTS.findIndex(d=>d.id===id);
  const darkBg = darkenHex(color, 0.32);
  const bgImg = DEPT_IMAGES[id];

  return (
    <div data-testid="department-slide-panel" className="flex w-full h-full pt-16 overflow-hidden">

      {/* ── LEFT PANEL ──────────────────────────────────────────────────── */}
      <motion.div
        className="relative flex flex-col justify-between overflow-hidden shrink-0"
        style={{
          width:"44%",
          background:`linear-gradient(150deg, ${darkBg} 0%, ${color} 100%)`,
          clipPath:"polygon(0 0, 100% 0, calc(100% - 48px) 100%, 0 100%)",
          padding:"40px 60px 40px 44px",
          zIndex:2,
        }}
        initial={{opacity:0, x:-80}}
        animate={{opacity:1, x:0}}
        transition={{duration:0.55, ease:[0.22,1,0.36,1]}}
      >
        <div className="absolute inset-0 opacity-[0.06]"
          style={{backgroundImage:"radial-gradient(circle at 1px 1px, white 1px, transparent 0)",backgroundSize:"32px 32px"}}/>

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="rounded-sm p-3" style={{background:"rgba(0,0,0,0.25)"}}>
              <Icon size={32} className="text-white" strokeWidth={1.5}/>
            </div>
            <div className="font-black text-white/40" style={{fontFamily:"JetBrains Mono,monospace",fontSize:"13px",letterSpacing:"0.1em"}}>
              {String(deptIdx+1).padStart(2,"0")} / {DEPTS.length}
            </div>
          </div>

          <motion.h2
            initial={{opacity:0,x:-50}} animate={{opacity:1,x:0}}
            transition={{duration:0.55,ease:[0.22,1,0.36,1],delay:0.15}}
            className="text-white font-black leading-none"
            style={{fontSize:"clamp(36px,4vw,56px)",letterSpacing:"-0.03em",lineHeight:0.95}}>
            {name}
          </motion.h2>
          <motion.p
            initial={{opacity:0,x:-40}} animate={{opacity:1,x:0}}
            transition={{duration:0.5,delay:0.25}}
            className="text-white/70 mt-3 font-medium"
            style={{fontFamily:"JetBrains Mono,monospace",fontSize:"13px",letterSpacing:"0.04em"}}>
            {tagline}
          </motion.p>

          <motion.div
            initial={{opacity:0,y:16}} animate={{opacity:1,y:0}}
            transition={{duration:0.5,delay:0.35}}
            className="flex gap-8 mt-10">
            {metrics.map((m,i)=>(
              <div key={i} className="flex flex-col">
                <div className="text-white font-black" style={{fontFamily:"JetBrains Mono,monospace",fontSize:"44px",lineHeight:1}}>
                  {m.raw ? m.raw : <AnimatedCounter to={m.to} suffix={m.suffix||""}/>}
                </div>
                <div className="text-white/55 uppercase tracking-widest mt-1" style={{fontFamily:"JetBrains Mono,monospace",fontSize:"10px"}}>
                  {m.label}
                </div>
              </div>
            ))}
          </motion.div>

          <motion.div
            initial={{opacity:0,x:-40}} animate={{opacity:1,x:0}}
            transition={{duration:0.5,delay:0.45}}
            className="mt-8 rounded-sm p-4"
            style={{background:"rgba(0,0,0,0.28)"}}>
            <div className="text-white/50 uppercase tracking-widest mb-1.5" style={{fontFamily:"JetBrains Mono,monospace",fontSize:"9px"}}>
              Innovation
            </div>
            <p className="text-white/85 leading-relaxed" style={{fontSize:"12.5px"}}>{innovation}</p>
          </motion.div>
        </div>

        <motion.div
          initial={{opacity:0,y:20}} animate={{opacity:1,y:0}}
          transition={{duration:0.5,delay:0.55}}
          className="relative z-10 space-y-3">
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm"
            style={{background:"rgba(0,0,0,0.3)",fontSize:"10px",fontFamily:"JetBrains Mono,monospace",color:"rgba(255,255,255,0.7)"}}>
            <ShieldCheck size={11} strokeWidth={2}/> DGCA · {dgca}
          </div>
          <button onClick={()=>onNavigate(route)} data-testid="open-in-platform-button"
            className="flex items-center gap-2 px-4 py-2.5 text-white font-bold transition-all hover:scale-[1.03] w-fit rounded-sm"
            style={{background:"rgba(255,255,255,0.15)",backdropFilter:"blur(8px)",
              fontFamily:"JetBrains Mono,monospace",fontSize:"11px",letterSpacing:"0.06em",border:"1px solid rgba(255,255,255,0.2)"}}>
            <ExternalLink size={12}/> Open in Platform
          </button>
        </motion.div>
      </motion.div>

      {/* ── RIGHT PANEL ────────────────────────────────────────────────── */}
      <div className="flex-1 relative overflow-hidden flex flex-col justify-center"
        style={{background:"#060d1a",paddingLeft:"72px",paddingRight:"56px",paddingTop:"32px",paddingBottom:"32px"}}>

        {/* Background image — visible on the right side where there is no text */}
        {bgImg && (
          <>
            <div className="absolute inset-0"
              style={{backgroundImage:`url(${bgImg})`,backgroundSize:"cover",backgroundPosition:"center right",opacity:0.45}}/>
            {/* Gradient: dark on LEFT (text area) → transparent on RIGHT (image area) */}
            <div className="absolute inset-0"
              style={{background:"linear-gradient(to left, transparent 0%, rgba(6,13,26,0.55) 40%, #060d1a 68%)"}}/>
            {/* Top + bottom fade */}
            <div className="absolute inset-0"
              style={{background:"linear-gradient(to bottom, rgba(6,13,26,0.6) 0%, transparent 18%, transparent 75%, rgba(6,13,26,0.8) 100%)"}}/>
          </>
        )}

        {/* Watermark dept number */}
        <div className="absolute -bottom-8 -right-6 font-black leading-none select-none pointer-events-none"
          style={{fontSize:"220px",color,opacity:0.04,fontFamily:"JetBrains Mono,monospace"}}>
          {String(deptIdx+1).padStart(2,"0")}
        </div>
        <ColorOrbs color={color}/>

        <div className="relative z-10 max-w-2xl">
          <motion.div variants={fromLeft(0.2)} initial="hidden" animate="show" className="mb-7">
            <div className="text-[10px] uppercase tracking-[0.15em] font-bold mb-2.5"
              style={{color:"#ef4444",fontFamily:"JetBrains Mono,monospace"}}>The Challenge</div>
            <p style={{color:"#94a3b8",fontSize:"16px",lineHeight:1.7}}>{problem}</p>
          </motion.div>

          <motion.div variants={fromLeft(0.3)} initial="hidden" animate="show" className="mb-7">
            <div className="text-[10px] uppercase tracking-[0.15em] font-bold mb-2.5"
              style={{color,fontFamily:"JetBrains Mono,monospace"}}>Fatigue Shield Approach</div>
            <p className="font-semibold text-white" style={{fontSize:"17px",lineHeight:1.65}}>{solution}</p>
          </motion.div>

          <motion.div variants={fromLeft(0.38)} initial="hidden" animate="show"
            className="mb-6 h-px" style={{background:`linear-gradient(to right, ${color}40, transparent)`}}/>

          <motion.ul variants={featureContainer} initial="hidden" animate="show" className="space-y-4">
            {features.map((f,i)=>(
              <motion.li key={i} variants={featureItem} className="flex items-start gap-3">
                <CheckCircle size={17} className="mt-0.5 shrink-0" style={{color}} strokeWidth={2.5}/>
                <span style={{color:"#cbd5e1",fontSize:"15.5px",lineHeight:1.55}}>{f}</span>
              </motion.li>
            ))}
          </motion.ul>
        </div>
      </div>
    </div>
  );
}

// ─── CTA Slide ────────────────────────────────────────────────────────────────
const COMPETITORS = [
  { name:"Jeppesen Crew",   company:"Boeing",            cost:"$3 – 7M USD",  note:"No DGCA module" },
  { name:"CAE CrewLink",    company:"CAE Inc.",          cost:"$4 – 8M USD",  note:"FAA/EASA-centric" },
  { name:"AIMS",            company:"SITA / AerData",    cost:"$2 – 5M USD",  note:"Requires India adaptation" },
  { name:"NetLine/Crew",    company:"Lufthansa Systems", cost:"€2 – 5M",      note:"European regulatory model" },
  { name:"iFlight",         company:"IBS Software",      cost:"$3 – 6M USD",  note:"Significant customisation" },
];

const INDIA_STATS = [
  { label:"Largest Aviation Market",   value:"3rd",     sub:"Globally by passengers (2024)" },
  { label:"Commercial Aircraft",       value:"700+",    sub:"In scheduled service today" },
  { label:"Licensed Pilots",           value:"6,000+",  sub:"Active on Indian registers" },
  { label:"Passengers by 2040",        value:"1 Billion",sub:"IATA India projection" },
  { label:"Indian-built Crew Systems", value:"ZERO",    sub:"DGCA-certified today" },
];

const PARTNER_CARDS = [
  {
    Icon: Plane,
    color: "#3b82f6",
    who: "For Airlines",
    title: "Pilot Programme Partner",
    desc: "Six-month fully operational pilot — zero licensing cost. Your operations data, your DGCA requirements, your real disruptions. We configure, you fly.",
    cta: "Request a Demo",
  },
  {
    Icon: ShieldCheck,
    color: "#22c55e",
    who: "For DGCA / AAI",
    title: "Regulatory Sandbox",
    desc: "Partner with us on DGCA accreditation. We'll align every algorithm, every compliance rule, and every audit trail to the exact standard you set. First Indian FRMS certification pathway.",
    cta: "Schedule a Briefing",
  },
  {
    Icon: Target,
    color: "#f59e0b",
    who: "For Technology Partners",
    title: "Co-Development Partnership",
    desc: "Join as a founding technology partner. Co-fund, co-develop, and co-own the IP of India's first sovereign aviation operations platform. Perpetual licence for founding partners.",
    cta: "Explore Partnership",
  },
];

const CTA_BG = "https://images.unsplash.com/photo-1683971113886-ca5883e598b6?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1600";

function CTASlide() {
  return (
    <div className="relative w-full h-full overflow-y-auto pt-16" style={{background:"#030c1a"}}>
      {/* Background image */}
      <div className="absolute inset-0"
        style={{backgroundImage:`url(${CTA_BG})`,backgroundSize:"cover",backgroundPosition:"center",opacity:0.07}}/>
      <div className="absolute inset-0" style={{background:"linear-gradient(to bottom,#030c1a 0%,transparent 30%,transparent 70%,#030c1a 100%)"}}/>

      <div className="relative z-10 px-14 py-6">

        {/* Header */}
        <motion.div variants={fromLeft(0.05)} initial="hidden" animate="show" className="mb-6">
          <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-sm"
            style={{background:"rgba(239,68,68,0.12)",border:"1px solid rgba(239,68,68,0.3)",
              fontFamily:"JetBrains Mono,monospace",fontSize:"10px",color:"#fca5a5",letterSpacing:"0.12em"}}>
            <Globe size={11}/> THE BUSINESS CASE FOR INDIAN AVIATION TECHNOLOGY
          </div>
          <h2 className="text-white font-black uppercase leading-none"
            style={{fontSize:"clamp(28px,3.5vw,48px)",letterSpacing:"-0.03em"}}>
            India's Aviation Market Is the{" "}
            <span style={{color:"#3b82f6"}}>3rd Largest in the World.</span><br/>
            Why Is Its Safety Technology Still{" "}
            <span style={{color:"#ef4444"}}>Imported?</span>
          </h2>
        </motion.div>

        {/* India Stats Bar */}
        <motion.div variants={fromLeft(0.12)} initial="hidden" animate="show"
          className="flex gap-3 mb-6">
          {INDIA_STATS.map((s,i)=>(
            <div key={i} className="flex-1 rounded-sm p-3 text-center"
              style={{background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)"}}>
              <div className="font-black text-white" style={{fontFamily:"JetBrains Mono,monospace",fontSize:"clamp(14px,1.5vw,20px)",lineHeight:1}}>
                {s.value}
              </div>
              <div className="text-[#60a5fa] font-bold mt-1" style={{fontSize:"10px"}}>{s.label}</div>
              <div className="text-[#64748b]" style={{fontSize:"9px",fontFamily:"JetBrains Mono,monospace"}}>{s.sub}</div>
            </div>
          ))}
        </motion.div>

        {/* Two-column: Competitors vs Fatigue Shield */}
        <div className="grid grid-cols-2 gap-5 mb-6">

          {/* Left: Foreign systems */}
          <motion.div variants={fromLeft(0.18)} initial="hidden" animate="show">
            <div className="flex items-center gap-2 mb-3">
              <AlertOctagon size={14} style={{color:"#ef4444"}}/>
              <div className="text-[10px] uppercase tracking-[0.15em] font-bold"
                style={{color:"#ef4444",fontFamily:"JetBrains Mono,monospace"}}>
                Foreign Crew Management Systems — Estimated 5-Year TCO per Carrier
              </div>
            </div>
            <div className="rounded-sm overflow-hidden" style={{border:"1px solid rgba(239,68,68,0.2)"}}>
              <div className="grid grid-cols-3 text-[9px] uppercase tracking-widest font-bold px-3 py-2"
                style={{background:"rgba(239,68,68,0.15)",color:"#fca5a5",fontFamily:"JetBrains Mono,monospace"}}>
                <span>System</span><span>Est. Cost</span><span>DGCA-Native?</span>
              </div>
              {COMPETITORS.map((c,i)=>(
                <div key={i} className="grid grid-cols-3 items-center px-3 py-2.5"
                  style={{background:i%2===0?"rgba(255,255,255,0.02)":"rgba(255,255,255,0.01)",
                    borderTop:"1px solid rgba(239,68,68,0.1)"}}>
                  <div>
                    <div className="text-white font-bold" style={{fontSize:"12px"}}>{c.name}</div>
                    <div className="text-[#64748b]" style={{fontSize:"9px",fontFamily:"JetBrains Mono,monospace"}}>{c.company}</div>
                  </div>
                  <div className="font-black" style={{color:"#ef4444",fontFamily:"JetBrains Mono,monospace",fontSize:"13px"}}>{c.cost}</div>
                  <div className="flex items-center gap-1">
                    <X size={11} style={{color:"#ef4444"}}/>
                    <span style={{color:"#94a3b8",fontSize:"10px"}}>{c.note}</span>
                  </div>
                </div>
              ))}
              <div className="px-3 py-2 text-[9px]" style={{background:"rgba(239,68,68,0.08)",color:"#fca5a5",fontFamily:"JetBrains Mono,monospace"}}>
                * Estimates based on publicly available pricing and industry benchmarks. Actual costs vary by carrier size and contract terms.
              </div>
            </div>
          </motion.div>

          {/* Right: Fatigue Shield */}
          <motion.div variants={fromLeft(0.24)} initial="hidden" animate="show">
            <div className="flex items-center gap-2 mb-3">
              <Star size={14} style={{color:"#22c55e"}}/>
              <div className="text-[10px] uppercase tracking-[0.15em] font-bold"
                style={{color:"#22c55e",fontFamily:"JetBrains Mono,monospace"}}>
                Fatigue Shield — Built in India. For India.
              </div>
            </div>
            <div className="rounded-sm p-4 h-full"
              style={{background:"rgba(34,197,94,0.06)",border:"1px solid rgba(34,197,94,0.2)"}}>
              <div className="space-y-2.5">
                {[
                  { icon:MapPin,  text:"Built by Indian aviation professionals for Indian operations" },
                  { icon:ShieldCheck, text:"DGCA-native from day one — no costly foreign-framework adaptation" },
                  { icon:Brain,   text:"Claude AI + Indian operational intelligence baked into every module" },
                  { icon:Shield,  text:"Full data sovereignty — hosted on Indian infrastructure, DPDP-ready" },
                  { icon:CheckCircle, text:"Full-spectrum FRMS: flight crew, AMEs, and ground staff all covered" },
                  { icon:TrendingUp, text:"Fraction of foreign system cost — founding partner pricing available" },
                  { icon:Users,   text:"Designed for IndiGo · Air India · SpiceJet · regional carriers" },
                ].map((item,i)=>(
                  <div key={i} className="flex items-start gap-2.5">
                    <item.icon size={14} className="mt-0.5 shrink-0" style={{color:"#22c55e"}}/>
                    <span style={{color:"#cbd5e1",fontSize:"12.5px",lineHeight:1.5}}>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Partnership CTA Cards */}
        <motion.div variants={fromLeft(0.3)} initial="hidden" animate="show">
          <div className="text-center mb-4">
            <div className="inline-flex items-center gap-2 px-4 py-1 rounded-sm mb-2"
              style={{background:"rgba(59,130,246,0.12)",border:"1px solid rgba(59,130,246,0.25)",
                fontFamily:"JetBrains Mono,monospace",fontSize:"10px",color:"#60a5fa",letterSpacing:"0.1em"}}>
              THE PARTNERSHIP OPPORTUNITY
            </div>
            <p className="text-[#8ba3cb]" style={{fontSize:"13px"}}>
              We are seeking Indian aviation stakeholders to co-develop, certify, and deploy this platform across the subcontinent.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {PARTNER_CARDS.map((card,i)=>(
              <div key={i} className="rounded-sm p-4"
                style={{background:"rgba(255,255,255,0.04)",border:`1px solid ${card.color}30`}}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-sm flex items-center justify-center"
                    style={{background:`${card.color}18`,border:`1px solid ${card.color}40`}}>
                    <card.Icon size={14} style={{color:card.color}}/>
                  </div>
                  <div style={{fontFamily:"JetBrains Mono,monospace",fontSize:"9px",color:card.color,letterSpacing:"0.1em",textTransform:"uppercase"}}>
                    {card.who}
                  </div>
                </div>
                <div className="text-white font-bold mb-1.5" style={{fontSize:"13px"}}>{card.title}</div>
                <p className="text-[#8ba3cb] mb-3" style={{fontSize:"11.5px",lineHeight:1.55}}>{card.desc}</p>
                <div className="flex items-center gap-1.5 font-bold" style={{color:card.color,fontSize:"11px",fontFamily:"JetBrains Mono,monospace"}}>
                  {card.cta} <ArrowRight size={11}/>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}

// ─── Closing Slide ────────────────────────────────────────────────────────────
function ClosingSlide({ onExit }) {
  const highlights=[
    {label:"Claude AI Fleet Optimizer",desc:"37 B737 tails optimised in real-time — 3-view Gantt with teal overlay and gap analysis"},
    {label:"Live Network Ops Map",desc:"Interactive India route map with AOG cascade flows, colour-coded disruption chains, and recovery console"},
    {label:"Fairness & QoL Engine",desc:"QoL Index per crew member — scheduling burden tracking, base comparisons, and extremes identification"},
    {label:"DGCA-Native Design",desc:"Every module built around Indian aviation regulatory requirements — no foreign-framework adaptation"},
    {label:"End-to-End Simulation",desc:"Trigger → Analyse → Resolve → Verify in one platform with full DGCA audit trail"},
  ];
  const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
  return (
    <div className="flex flex-col items-center justify-center h-full px-16 pt-16 pb-10 relative overflow-hidden"
      style={{background:"#060d1a"}}>
      <div className="absolute inset-0 opacity-[0.03]"
        style={{backgroundImage:"linear-gradient(#3b82f6 1px,transparent 1px),linear-gradient(90deg,#3b82f6 1px,transparent 1px)",backgroundSize:"80px 80px"}}/>
      <ColorOrbs color="#3b82f6"/>

      <motion.div variants={fromLeft(0.1)} initial="hidden" animate="show" className="relative z-10 mb-10 self-start">
        <div className="inline-flex items-center gap-2 px-3 py-1 mb-5"
          style={{border:"1px solid rgba(59,130,246,0.3)",background:"rgba(59,130,246,0.1)",
            fontFamily:"JetBrains Mono,monospace",fontSize:"10px",color:"#60a5fa",letterSpacing:"0.1em",borderRadius:"2px"}}>
          PLATFORM SUMMARY
        </div>
        <h2 className="text-white font-black uppercase"
          style={{fontSize:"clamp(36px,5vw,64px)",lineHeight:0.92,letterSpacing:"-0.03em"}}>
          Built for India's<br/>
          <span style={{color:"#3b82f6"}}>Aviation Future</span>
        </h2>
        <p className="mt-4 text-[#8ba3cb]" style={{fontSize:"18px"}}>
          22+ modules · 275+ crew · DGCA-aligned · Claude AI · Real-time ops
        </p>
      </motion.div>

      <motion.div variants={fromLeft(0.3)} initial="hidden" animate="show"
        className="grid grid-cols-5 gap-4 w-full relative z-10">
        {highlights.map((h,i)=>(
          <div key={i} className="rounded-sm border p-5" style={{borderColor:"#1a2642",background:"#0a1122"}}>
            <div style={{width:"32px",height:"3px",background:"#3b82f6",marginBottom:"12px",borderRadius:"2px"}}/>
            <div className="text-white font-bold mb-2" style={{fontSize:"14px"}}>{h.label}</div>
            <div className="text-[#8ba3cb]" style={{fontSize:"12px",lineHeight:1.6}}>{h.desc}</div>
          </div>
        ))}
      </motion.div>

      <motion.div variants={fromLeft(0.5)} initial="hidden" animate="show"
        className="flex items-center gap-6 mt-10 relative z-10 self-start flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#8ba3cb] mb-2"
            style={{fontFamily:"JetBrains Mono,monospace"}}>Demo Access</div>
          <div className="text-white font-mono text-sm border rounded-sm px-5 py-2.5"
            style={{borderColor:"#1a2642",background:"#0a1122",fontSize:"14px"}}>
            admin@airline.com · Admin@123
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#8ba3cb] mb-2"
            style={{fontFamily:"JetBrains Mono,monospace"}}>Download Full PDF Report</div>
          <a href={`${API_BASE}/api/platform/demo-pdf`} target="_blank" rel="noreferrer"
            data-testid="download-pdf-button"
            className="flex items-center gap-2 px-5 py-2.5 text-white font-bold rounded-sm border transition-all hover:scale-[1.03]"
            style={{borderColor:"#22c55e",background:"rgba(34,197,94,0.12)",fontFamily:"JetBrains Mono,monospace",fontSize:"12px",letterSpacing:"0.06em"}}>
            <Download size={14}/> Download PDF Deck
          </a>
        </div>
        <button onClick={onExit} data-testid="explore-platform-button"
          className="px-10 py-4 text-white font-bold uppercase tracking-widest transition-all hover:scale-[1.03] rounded-sm"
          style={{background:"#3b82f6",fontFamily:"JetBrains Mono,monospace",fontSize:"13px",
            boxShadow:"0 0 40px rgba(59,130,246,0.35)"}}>
          Explore the Platform <ChevronRight size={14} className="inline ml-1"/>
        </button>
      </motion.div>
    </div>
  );
}

// ─── Glossary Panel ───────────────────────────────────────────────────────────
function GlossaryPanel({ onClose }) {
  return (
    <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
      className="fixed inset-0 z-[200] flex items-center justify-center"
      style={{background:"rgba(3,12,26,0.97)",backdropFilter:"blur(20px)"}}
      onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} className="w-full max-w-5xl px-8 max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-white font-bold text-xl tracking-tight">Abbreviations & Glossary</h3>
            <p className="text-[#64748b] text-sm mt-0.5" style={{fontFamily:"JetBrains Mono,monospace"}}>
              All abbreviations used in this presentation
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-white/50 hover:text-white"><X size={18}/></button>
        </div>
        <div className="overflow-y-auto flex-1">
          <div className="grid grid-cols-2 gap-2 pb-4">
            {GLOSSARY.map((g,i)=>(
              <div key={i} className="flex gap-3 p-3 rounded-sm"
                style={{background:"#0a1122",border:"1px solid #1a2642"}}>
                <div className="shrink-0 w-16 font-black text-[#3b82f6]"
                  style={{fontFamily:"JetBrains Mono,monospace",fontSize:"12px",letterSpacing:"0.05em"}}>
                  {g.abbr}
                </div>
                <p className="text-[#94a3b8]" style={{fontSize:"12px",lineHeight:1.55}}>{g.full}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ─── Top Navigation ───────────────────────────────────────────────────────────
function TopNav({ current,total,slides,autoPlay,onToggleAutoPlay,onShowMenu,onShowGlossary,onExit,onGoTo }) {
  const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
  return (
    <div className="absolute top-0 w-full h-16 px-6 flex items-center justify-between z-50"
      style={{borderBottom:"1px solid rgba(255,255,255,0.06)",background:"rgba(6,13,26,0.9)",backdropFilter:"blur(12px)"}}>
      <div className="flex items-center gap-2.5">
        <div className="w-7 h-7 rounded-sm flex items-center justify-center" style={{background:"#3b82f6"}}>
          <Plane size={13} className="text-white"/>
        </div>
        <span className="text-white font-black text-sm tracking-tight">FATIGUE SHIELD</span>
        <span className="text-white/30 text-xs ml-2" style={{fontFamily:"JetBrains Mono,monospace"}}>
          {current+1} / {total}
        </span>
      </div>

      <div data-testid="progress-dot-indicator" className="flex items-center gap-1.5">
        {slides.map((_,i)=>(
          <button key={i} onClick={()=>onGoTo(i)} title={slides[i]?.data?.name||(slides[i]?.type==="hero"?"Introduction":slides[i]?.type==="science"?"Algorithm":"Summary")}
            className="rounded-full transition-all duration-500"
            style={{height:"5px",width:i===current?"28px":"5px",
              background:i===current?"#3b82f6":i<current?"rgba(59,130,246,0.4)":"rgba(255,255,255,0.15)"}}/>
        ))}
      </div>

      <div className="flex items-center gap-1">
        <a href={`${API_BASE}/api/platform/demo-pdf`} target="_blank" rel="noreferrer"
          data-testid="nav-download-pdf-button"
          title="Download PDF Deck"
          className="p-2 rounded text-white/50 hover:text-white hover:bg-white/5 transition-colors">
          <Download size={15}/>
        </a>
        <button onClick={onShowGlossary} data-testid="glossary-button"
          title="Abbreviations Glossary"
          className="p-2 rounded text-white/50 hover:text-white hover:bg-white/5 transition-colors">
          <BookOpen size={15}/>
        </button>
        <button onClick={onToggleAutoPlay} data-testid="autoplay-toggle-button"
          className="p-2 rounded text-white/50 hover:text-white hover:bg-white/5 transition-colors">
          {autoPlay?<Pause size={15}/>:<Play size={15}/>}
        </button>
        <button onClick={onShowMenu} data-testid="jump-menu-button"
          className="p-2 rounded text-white/50 hover:text-white hover:bg-white/5 transition-colors">
          <MenuIcon size={15}/>
        </button>
        <button onClick={onExit} data-testid="close-presentation-button"
          className="p-2 rounded text-white/50 hover:text-white hover:bg-white/5 transition-colors">
          <X size={15}/>
        </button>
      </div>
    </div>
  );
}

// ─── Bottom Controls ──────────────────────────────────────────────────────────
function BottomControls({ onPrev,onNext,current,total }) {
  return (
    <div className="absolute bottom-0 w-full px-8 pb-6 flex items-center justify-between z-50 pointer-events-none">
      <button onClick={onPrev} data-testid="slide-prev-button"
        disabled={current===0}
        className="p-3 rounded-full border transition-colors disabled:opacity-20 pointer-events-auto"
        style={{borderColor:"rgba(255,255,255,0.12)",color:"rgba(255,255,255,0.5)"}}>
        <ChevronLeft size={18}/>
      </button>
      <div className="text-white/20 text-xs" style={{fontFamily:"JetBrains Mono,monospace"}}>
        {current>0&&current<total-1 ? ALL_SLIDES[current]?.data?.name||(ALL_SLIDES[current]?.type==="science"?"Algorithm":ALL_SLIDES[current]?.type==="cta"?"Partnership CTA":"") : ""}
      </div>
      <button onClick={onNext} data-testid="slide-next-button"
        disabled={current===total-1}
        className="p-3 rounded-full border transition-colors disabled:opacity-20 pointer-events-auto"
        style={{borderColor:"rgba(255,255,255,0.12)",color:"rgba(255,255,255,0.5)"}}>
        <ChevronRight size={18}/>
      </button>
    </div>
  );
}

// ─── Jump Menu ────────────────────────────────────────────────────────────────
function JumpMenu({ current,onGoTo,onClose }) {
  const menuSlides = [
    {id:"hero",    name:"Intro",       Icon:Plane,      color:"#3b82f6", type:"hero"},
    {id:"science", name:"Algorithm",   Icon:Brain,      color:"#3b82f6", type:"science"},
    ...DEPTS.map(d=>({...d,type:"dept"})),
    {id:"cta",     name:"Partnership", Icon:Globe,      color:"#ef4444", type:"cta"},
    {id:"closing", name:"Summary",     Icon:Activity,   color:"#3b82f6", type:"closing"},
  ];
  return (
    <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
      className="fixed inset-0 z-[200] flex items-center justify-center"
      style={{background:"rgba(3,12,26,0.97)",backdropFilter:"blur(20px)"}}
      onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} className="w-full max-w-5xl px-8">
        <div className="flex items-center justify-between mb-8">
          <h3 className="text-white font-bold text-xl tracking-tight">Jump to Section</h3>
          <button onClick={onClose} className="p-2 text-white/50 hover:text-white"><X size={18}/></button>
        </div>
        <div className="grid grid-cols-6 gap-3 xl:grid-cols-7">
          {menuSlides.map((s,i)=>{
            const Ic = s.Icon||Activity;
            const active = i===current;
            return (
              <button key={i} onClick={()=>onGoTo(i)}
                className="rounded-sm border p-3 text-left transition-all hover:scale-[1.03]"
                style={{borderColor:active?s.color:"#1a2642",background:active?`${s.color}20`:"#0a1122"}}>
                <Ic size={16} style={{color:s.color,marginBottom:"6px"}} strokeWidth={1.5}/>
                <div className="text-white text-xs font-bold leading-tight">{s.name||s.id}</div>
                <div className="text-white/30 mt-0.5" style={{fontFamily:"JetBrains Mono,monospace",fontSize:"9px"}}>
                  {String(i+1).padStart(2,"0")}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function PresentationMode() {
  useFonts();
  const navigate = useNavigate();
  const [current,setCurrent] = useState(0);
  const [direction,setDirection] = useState(1);
  const [autoPlay,setAutoPlay] = useState(false);
  const [showMenu,setShowMenu] = useState(false);
  const [showGlossary,setShowGlossary] = useState(false);
  const total = ALL_SLIDES.length;

  const goTo = useCallback((idx)=>{
    if(idx<0||idx>=total) return;
    setDirection(idx>=current?1:-1);
    setCurrent(idx);
  },[current,total]);
  const next = useCallback(()=>goTo(current+1),[current,goTo]);
  const prev = useCallback(()=>goTo(current-1),[current,goTo]);

  useEffect(()=>{
    const h = e=>{
      if(showGlossary||showMenu){ if(e.key==="Escape"){e.preventDefault();setShowMenu(false);setShowGlossary(false);} return; }
      if(e.key==="ArrowRight"||e.key===" "){e.preventDefault();next();}
      if(e.key==="ArrowLeft"){e.preventDefault();prev();}
      if(e.key==="Escape"){e.preventDefault();navigate("/dashboard");}
    };
    window.addEventListener("keydown",h);
    return ()=>window.removeEventListener("keydown",h);
  },[next,prev,navigate,showMenu,showGlossary]);

  useEffect(()=>{
    if(!autoPlay) return;
    const id=setInterval(()=>{setDirection(1);setCurrent(c=>(c+1)%total);},8000);
    return ()=>clearInterval(id);
  },[autoPlay,total]);

  const slide = ALL_SLIDES[current];

  return (
    <div data-testid="presentation-overlay"
      className="fixed inset-0 z-[9999] overflow-hidden text-white select-none"
      style={{background:"#060d1a",fontFamily:"'IBM Plex Sans',system-ui,sans-serif"}}>
      <TopNav current={current} total={total} slides={ALL_SLIDES} autoPlay={autoPlay}
        onToggleAutoPlay={()=>setAutoPlay(p=>!p)}
        onShowMenu={()=>setShowMenu(true)}
        onShowGlossary={()=>setShowGlossary(true)}
        onExit={()=>navigate("/dashboard")}
        onGoTo={goTo}/>
      <div className="absolute inset-0">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div key={current} custom={direction}
            variants={slideVariants} initial="enter" animate="center" exit="exit"
            transition={{duration:0.45,ease:[0.22,1,0.36,1]}}
            className="absolute inset-0">
            {slide.type==="hero"    && <HeroSlide onNext={next}/>}
            {slide.type==="science" && <ScienceSlide/>}
            {slide.type==="dept"    && <DeptSlide slide={slide.data} onNavigate={navigate}/>}
            {slide.type==="cta"     && <CTASlide/>}
            {slide.type==="closing" && <ClosingSlide onExit={()=>navigate("/dashboard")}/>}
          </motion.div>
        </AnimatePresence>
      </div>
      <BottomControls onPrev={prev} onNext={next} current={current} total={total}/>
      <AnimatePresence>
        {showMenu    && <JumpMenu current={current} onGoTo={i=>{goTo(i);setShowMenu(false);}} onClose={()=>setShowMenu(false)}/>}
        {showGlossary && <GlossaryPanel onClose={()=>setShowGlossary(false)}/>}
      </AnimatePresence>
    </div>
  );
}
